import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, EmptyState, Segmented, TableSkeleton } from "../../../components/ui";
import { getUserOverview } from "../../../services/users";
import { can } from "../../../utils/roles";
import PaymentDetail from "../payments-ledger/PaymentDetail";
import ThanksComposer from "../payments-ledger/ThanksComposer";
import { formatMoney, formatWhen, MethodLabel, StatusBadge, TypeLabel } from "../payments-ledger/shared";
import "../payments-ledger/payments.css";

const fmt = (n) => new Intl.NumberFormat().format(n || 0);
const day = (date) => (date ? new Date(date).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) : "—");
const LEVEL_COLORS = { error: "red", warn: "amber", info: "blue", debug: "slate" };

/**
 * Everything a user did on AFO MEDIA, below the account details: engagement
 * figures, then payments, videos watched, comments, quiz and exclusive
 * content, journal (admin actions, payments, reminders about the user).
 */
const UserOverview = ({ userId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("payments");
  const [opened, setOpened] = useState(null);
  const [thanking, setThanking] = useState(null);
  const canPayments = can("transactions");
  const canActPayments = can("transactions", "edit");

  const load = useCallback(() => {
    setError(false);
    return getUserOverview(userId)
      .then(setData)
      .catch(() => setError(true));
  }, [userId]);
  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  if (error) {
    return (
      <section className="ui-card detail-pad">
        <EmptyState icon="ri-error-warning-line">
          {t("The activity of this user could not be loaded.")}{" "}
          <button type="button" className="ui-btn ui-btn-secondary" onClick={load}>
            {t("Retry")}
          </button>
        </EmptyState>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="ui-card detail-pad">
        <TableSkeleton rows={5} />
      </section>
    );
  }

  const e = data.engagement;
  const card = (value, label, hint) => (
    <div className="ui-card ledger-stat">
      <strong>{value}</strong>
      <span>{label}</span>
      {hint && <small className="user-overview-hint">{hint}</small>}
    </div>
  );
  const paid = data.payments.totals.map((row) => formatMoney(row.total, row.currency)).join(" + ") || formatMoney(0, "USD");
  const tabs = [
    { value: "payments", label: `${t("Payments")} (${data.payments.items.length})` },
    { value: "watched", label: t("Videos watched") },
    { value: "comments", label: `${t("Comments")} (${e.comments + e.replies})` },
    { value: "quiz", label: t("Quiz and exclusive content") },
    { value: "journal", label: t("Journal") },
  ];

  return (
    <>
      <div className="ledger-stats user-overview-stats">
        {card(paid, t("Paid in total"), t("{{count}} successful payments", { count: data.payments.totals.reduce((s, r) => s + r.count, 0) }))}
        {card(`${e.activeDays30} / 30`, t("Active days (30 days)"), t("{{count}} days in total", { count: e.activeDays }))}
        {card(fmt(e.videos), t("Videos started"), t("{{count}} watched to the end", { count: e.completed }))}
        {card(fmt(e.comments + e.replies), t("Comments and replies"), t("{{count}} likes received", { count: e.likesReceived }))}
        {card(fmt(e.likes + e.commentLikes), t("Likes given"), t("{{count}} in « Watch later »", { count: e.watchlist }))}
        {card(fmt(e.thanks), t("Merci sent"), e.pushEnabled ? t("Push notifications on") : t("Push notifications off"))}
      </div>

      <section className="ui-card detail-pad user-overview">
        <Segmented label={t("Activity")} value={tab} onChange={setTab} options={tabs} />

        {tab === "payments" &&
          (!data.payments.items.length ? (
            <EmptyState icon="ri-money-dollar-circle-line">{t("No payment")}</EmptyState>
          ) : (
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>{t("Date")}</th>
                    <th>{t("Type")}</th>
                    <th>{t("Amount")}</th>
                    <th>{t("Payment method")}</th>
                    <th>{t("status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.items.map((item) => (
                    <tr key={item.id} className={canPayments ? "ledger-row" : undefined} onClick={canPayments ? () => setOpened(item.id) : undefined}>
                      <td className="ui-cell-muted">{formatWhen(item.createdAt)}</td>
                      <td>
                        <TypeLabel payment={item} />
                        {item.content && <span className="ui-media-meta">{item.content}</span>}
                      </td>
                      <td className="ledger-amount">
                        {formatMoney(item.amount, item.currency)}
                        {item.refundedAmount > 0 && <span className="ui-media-meta">{t("{{amount}} refunded", { amount: formatMoney(item.refundedAmount, item.currency) })}</span>}
                      </td>
                      <td className="ui-cell-muted">
                        <MethodLabel payment={item} />
                      </td>
                      <td>
                        <StatusBadge status={item.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

        {tab === "watched" &&
          (!data.watched.length ? (
            <EmptyState icon="ri-film-line">{t("No video watched yet")}</EmptyState>
          ) : (
            <ul className="user-overview-list">
              {data.watched.map((item, i) => (
                <li key={`${item.at}-${i}`}>
                  <span className="user-overview-main">{item.title}</span>
                  {item.percent !== null && (
                    <span className="user-overview-progress" title={`${item.percent} %`}>
                      <span style={{ width: `${item.percent}%` }}></span>
                    </span>
                  )}
                  <span className="ui-hint">{item.percent !== null ? `${item.percent} % · ` : ""}{day(item.at)}</span>
                </li>
              ))}
            </ul>
          ))}

        {tab === "comments" &&
          (!data.comments.length ? (
            <EmptyState icon="ri-chat-3-line">{t("No comment yet")}</EmptyState>
          ) : (
            <ul className="user-overview-list">
              {data.comments.map((item) => (
                <li key={item.id} className="user-overview-comment">
                  <span className="user-overview-main">« {item.text} »</span>
                  <span className="ui-hint">
                    {[item.reply ? t("Reply") : null, item.on, day(item.at), item.likes ? t("{{count}} likes", { count: item.likes }) : null].filter(Boolean).join(" · ")}
                  </span>
                  <span className="user-overview-badges">
                    {item.thanks && <Badge color="amber">{t("Merci")} {formatMoney(item.thanks.amount, item.thanks.currency)}</Badge>}
                    {item.hearted && <Badge color="red">♥ Alain Foka</Badge>}
                    {item.pinned && <Badge color="blue">{t("Pinned")}</Badge>}
                  </span>
                </li>
              ))}
            </ul>
          ))}

        {tab === "quiz" && (
          <div className="user-overview-columns">
            <div>
              <h3 className="detail-section-title">{t("Quiz")}</h3>
              {!data.quiz ? (
                <p className="ui-hint">{t("Has never played.")}</p>
              ) : (
                <>
                  <p>
                    {t("{{count}} games", { count: data.quiz.games })}
                    {data.quiz.best !== null ? ` · ${t("best score {{score}}", { score: data.quiz.best })} · ${t("average {{score}}", { score: String(data.quiz.average).replace(".", ",") })}` : ""}
                    {data.quiz.lastPlayedAt ? ` · ${t("last game {{date}}", { date: day(data.quiz.lastPlayedAt) })}` : ""}
                  </p>
                  {data.quiz.rewards.length > 0 && (
                    <ul className="user-overview-list">
                      {data.quiz.rewards.map((r) => (
                        <li key={r.at}>
                          <span className="user-overview-main">
                            {r.kind === "premium" ? t("{{count}} Premium days", { count: r.premiumDays }) : r.kind === "gift" ? r.giftLabel : r.tone === "encourage" ? t("Encouragement") : t("Congratulations")}
                          </span>
                          <span className="ui-hint">{[r.rank ? `N°${r.rank}` : null, day(r.at)].filter(Boolean).join(" · ")}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
            <div>
              <h3 className="detail-section-title">{t("Exclusive content bought")}</h3>
              {!data.exclusive.length ? (
                <p className="ui-hint">{t("None.")}</p>
              ) : (
                <ul className="user-overview-list">
                  {data.exclusive.map((item, i) => (
                    <li key={`${item.title}-${i}`}>
                      <span className="user-overview-main">{item.title}</span>
                      <Badge color={item.active ? "green" : "slate"}>
                        {item.active ? t("Access until {{date}}", { date: day(item.until) }) : t("Expired on {{date}}", { date: day(item.until) })}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === "journal" &&
          (!data.journal.length ? (
            <EmptyState icon="ri-file-list-3-line">{t("Nothing in the journal for this user")}</EmptyState>
          ) : (
            <ul className="user-overview-list">
              {data.journal.map((item, i) => (
                <li key={`${item.at}-${i}`}>
                  <Badge color={LEVEL_COLORS[item.level] || "slate"} dot>
                    {item.category}
                  </Badge>
                  <span className="user-overview-main">{item.message}</span>
                  <span className="ui-hint">{formatWhen(item.at)}</span>
                </li>
              ))}
            </ul>
          ))}
      </section>

      {opened && (
        <PaymentDetail
          id={opened}
          canAct={canActPayments}
          onClose={() => setOpened(null)}
          onThank={(payment) => {
            setOpened(null);
            setThanking([payment.id]);
          }}
          onChange={load}
        />
      )}
      {thanking && (
        <ThanksComposer
          ids={thanking}
          onClose={() => setThanking(null)}
          onSent={() => {
            setThanking(null);
            load();
          }}
        />
      )}
    </>
  );
};

export default UserOverview;
